/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2021, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import * as _ from 'underscore';
import { Logger } from '@core/lib/services';
import { Injectable, Injector } from '@angular/core';
import { DiService } from '@app/di/di.service';
import { IntegrationStatusService } from '@app/di/integration-status.service';
import { SignOutService } from '@app/common/sign-out.service';
import { ExternalAppContextService } from '@app/di/external-app-context.service';
import { TranslateService } from '@ngx-translate/core';
import { DiConfigService } from '@app/di/di-config.service';
import { XmlTemplateManagerService } from '@app/di/channel/lineshaft/xml-template-manager.service';
import { ContextService } from '@app/di/context.service';
import { MessageService } from '@core/lib/services/message.service';
import { UtilService } from '@app/util/util.service';
import { AuditService } from '@app/common/audit.service';
import { NotifyService } from '@app/common/notify.service';
import { EventMessage, Events, EventService, EventType } from '@app/common/event';
import { EventWorkItem } from '@app/data-model/event-work-item';
import { appConstants } from '@app/common/app-constants';
import { LineShaftIntegration, LineShaftHandlerService } from '@app/di/channel/lineshaft/line-shaft-handler.service';
import { UvDatetimeService } from './uv-datetime.service';
import {
  PerformanceMonitorService,
  PerformanceSource
} from '@app/ui/masthead/performance-monitor/performance-monitor.service';

const log = new Logger('UniversalViewerService');

@Injectable({
  providedIn: 'root'
})
export class UniversalViewerService implements LineShaftIntegration {
  private lineShaftHandlerService: LineShaftHandlerService;
  private tabCloseId;
  private viewerRequestRetryCount = 0;
  private viewerSetContextTimeout = null;
  private viewerMessageQueueParams;
  private loadMessageQueueParams;
  private UVIEWER_CONFIG;
  private tabs;
  private uvModuleName;
  private refresh = false;

  constructor(
    private injector: Injector,
    private diService: DiService,
    private integrationStatusService: IntegrationStatusService,
    private signOutService: SignOutService,
    private externalAppContext: ExternalAppContextService,
    private translate: TranslateService,
    private diConfigService: DiConfigService,
    private xmlTemplateManagerService: XmlTemplateManagerService,
    private notify: NotifyService,
    private contextService: ContextService,
    private messageService: MessageService,
    private auditService: AuditService,
    private utilService: UtilService,
    private eventService: EventService,
    private uvDateTimeService: UvDatetimeService
  ) {}

  public init() {
    log.info('App Initializing...');
    // Communication channel
    this.lineShaftHandlerService = this.injector.get(LineShaftHandlerService);
    this.lineShaftHandlerService.registerCleanupCallback(this.cleanup);

    // Configs
    this.setConfigValues();
    this.uvDateTimeService.getUVDateTimePref();
    // Listeners
    this.subscribeListeners();
    this.beforeEventActions();
    this.afterEventActions();
  }

  private isViewerConfigInvalid() {
    log.info("DI UV Config - ModuleName :'" + this.uvModuleName + "'");
    if (UtilService.isEmpty(this.uvModuleName)) {
      log.error(this.translate.instant('di.desktopIntegration.critical.incorrectViewerConfig'));
      return true;
    }
    return false;
  }

  subscribePluginResponse(retObj: any) {
    try {
      if (this.diService.hasXMLConfError('UV') || this.isViewerConfigInvalid()) {
        this.integrationStatusService.setUViewerPluginStatus(true);
        this.integrationStatusService.setViewerHealthStatus(
          this.integrationStatusService.healthStatusEnum.failed,
          true
        );
        log.error('Incorrect viewer configuration - Viewer disabled');
        this.notify.critical(
          this.translate.instant('di.desktopIntegration.critical.incorrectViewerConfig'),
          this.translate.instant('di.desktopIntegration.titleViewerDisabled')
        );
      } else {
        // Subscribe the viewer error response from viewer
        const viewerErrorResponse = this.xmlTemplateManagerService.getSubscribeXml(
          this.UVIEWER_CONFIG.OWNERINSTANCE + '.VIEWER_ERROR',
          ''
        );
        log.info('Subscribe the viewer error response from viewer');
        this.diService.loadPluginsInvokeAPIWrapperExecute(viewerErrorResponse);

        // Subscribe response from LineShaftPluginManager to get load response error due to the missing of
        // viewer plugin
        const viewerPluginLoadResponseFromPluginManager = this.xmlTemplateManagerService.getSubscribeXml(
          this.UVIEWER_CONFIG.OWNERINSTANCE + '.LS_PLUGIN_LOAD_RESPONSE',
          'LineShaftPluginManager'
        );
        this.diService.loadPluginsInvokeAPIWrapperExecute(viewerPluginLoadResponseFromPluginManager);

        // Subscribe the setcontext response from ViewerPlugin
        const setcontextResponse = this.xmlTemplateManagerService.getSubscribeXml(
          this.UVIEWER_CONFIG.OWNERINSTANCE + '.LS_PLUGIN_SETCONTEXT_RESPONSE',
          this.UVIEWER_CONFIG.MODULENAME
        );
        log.info('Subscribe the setcontext response from ViewerPlugin');
        this.diService.loadPluginsInvokeAPIWrapperExecute(setcontextResponse);

        const setViewerClose = this.xmlTemplateManagerService.getSubscribeXml(
          this.UVIEWER_CONFIG.OWNERINSTANCE + '.LS_VIEWER_CLOSED',
          this.UVIEWER_CONFIG.MODULENAME
        );
        log.info('Subscribe the setcontext response from ViewerPlugin');
        this.diService.loadPluginsInvokeAPIWrapperExecute(setViewerClose);

        // Subscribe the doc scan response from ViewerPlugin
        const docScanResponse = this.xmlTemplateManagerService.getSubscribeXml(
          'LS_DOC_SCAN_STATUS',
          this.UVIEWER_CONFIG.MODULENAME
        );
        log.info('Subscribe the Doc Scan response from ViewerPlugin');
        this.diService.loadPluginsInvokeAPIWrapperExecute(docScanResponse);
      }
    } catch (errorSubscribeViewerPlugin) {
      retObj.loadErrorEntity.push('Viewer');
      this.integrationStatusService.setViewerHealthStatus(this.integrationStatusService.healthStatusEnum.failed, true);
      log.error('loadPlugins() ViewerPlugin :' + errorSubscribeViewerPlugin);
    }
  }

  loadPlugin(retObj?: any) {
    if (
      !_.contains(retObj.loadErrorEntity, 'Viewer') &&
      !this.diService.hasXMLConfError('UV') &&
      !this.integrationStatusService.getUViewerPluginStatus()
    ) {
      try {
        // Create the viewer load xml and load viewer plugin
        const viewerLoadXML = this.xmlTemplateManagerService.getPluginLoadXml(this.UVIEWER_CONFIG.MODULENAME);
        log.info('load viewer plugin');
        const viewerLoadMessage = this.diService.getLSMessage(this.diService.getLSMessageType().LOAD, viewerLoadXML);
        viewerLoadMessage.responseSource = [this.UVIEWER_CONFIG.PLUGIN_MANAGER];
        viewerLoadMessage.moduleName = this.UVIEWER_CONFIG.MODULENAME;
        this.diService.loadPluginsInvokeAPIWrapper(viewerLoadMessage);
      } catch (errorLoadViewerPlugin) {
        retObj.loadErrorEntity.push('Viewer');
        this.integrationStatusService.setViewerHealthStatus(
          this.integrationStatusService.healthStatusEnum.failed,
          true
        );
        log.error('loadPlugins() ViewerPlugin :' + errorLoadViewerPlugin);
      }
    }
  }

  handleLaunchRequest(context, readOnlyFlag) {
    if (this.checkandAbortViewerLaunch(context, readOnlyFlag)) {
      return;
    }
    const priorContext = this.contextService.getPriorContext(context.id);
    if (priorContext) {
      priorContext.keys.studyClickTime = context.keys.studyClickTime;
      context = priorContext;
    }

    const workItem = this.createUViewerIntegrationContext(context, readOnlyFlag);
    // Check for null context
    const previousNullContext = null === this.contextService.getCurrentContext();
    // Get previous context
    const prevContext = this.contextService.getCurrentContext();
    // Check previous study data is available or not
    const prevStudyAvailable = this.contextService.isStudyDataAvailable(prevContext);

    this.diConfigService.getJwtToken().then(jwtToken => {
      this.contextService.setContext(workItem);

      // Setting launch time
      this.contextService.setStudyClickTime(workItem.studyClickTime);
      PerformanceMonitorService.clear(PerformanceSource.VIEWER);

      // Setting the close flag as false to take action on DONE option when closing the exams manually.
      this.contextService.setCloseFlag(false);

      if (this.skipUViewerLaunch(context, workItem, prevContext, previousNullContext, prevStudyAvailable, jwtToken)) {
        return;
      }
      // Launch viewer if study data is available and viewer is enabled.
      log.info(
        'handleLaunchRequest: ImageCount: ' +
          workItem.imageCount +
          ' RejectedImageCount: ' +
          workItem.rejectedImageCount
      );
      // No need to look at image count if exam is archive adopted
      if (
        workItem.archiveAdoptedStatus === 'YES' ||
        (UtilService.isExamOnline(workItem.studyDataAvailability) && workItem.imageCount > 0)
      ) {
        log.info('InvokeAPI - Open Viewer for id ' + workItem.requestedProcedureId);
        // Launch Viewer
        const launchXML = this.xmlTemplateManagerService.getViewerSetContextXml(jwtToken);
        PerformanceMonitorService.start(PerformanceSource.VIEWER, this.contextService.getCurrentContext());
        this.viewerInvokeAPIWrapper(launchXML, workItem.requestedProcedureId, '', 'UViewer');
      } else if (UtilService.isExamOnline(workItem.studyDataAvailability) && workItem.rejectedImageCount > 0) {
        log.info('handleLaunchRequest : rejected images only');
        this.notify.warning(
          this.translate.instant('di.desktopIntegration.warning.rejectedStudy'),
          this.translate.instant('di.desktopIntegration.title')
        );
        this.hideViewerForPrevContextAndStudy(previousNullContext, prevStudyAvailable, jwtToken);
      } else {
        log.info('handleLaunchRequest : zero image study, previousNullContext: ' + previousNullContext);
        this.hideViewerForPrevContextAndStudy(previousNullContext, prevStudyAvailable, jwtToken);
      }
    });
  }

  handlePreviewRequest(target) {
    if (this.diService.isSupportedBrowser() === false) {
      return;
    }
    const studyId = target.id;
    if (
      this.diService.getApplicationContext() !== null &&
      this.diService.getApplicationContext().toString() !== studyId.toString()
    ) {
      log.warn(
        'preview action handler - context (' +
          studyId +
          ') does not match with active work item (' +
          this.diService.getApplicationContext() +
          '), ignore preview request'
      );
      return;
    }
    const currentContext = this.contextService.getCurrentContext();
    // If no study is opened.
    if (null === currentContext) {
      log.info('handlePreviewRequest event : ' + studyId + ' ; no current context');
    } else if (studyId !== this.contextService.getCurrentContext()) {
      // A viewer for a different context is showing
      if (!this.uvInitialized()) {
        log.error('handlePreviewRequest - viewer initialization in progress');
        return;
      } else {
        log.info('handlePreviewRequest - viewer initialization success');
      }

      PerformanceMonitorService.clear(PerformanceSource.VIEWER);

      // Check the study data availability
      const studyAvailable = this.contextService.isStudyDataAvailable(this.contextService.getCurrentContext());

      // Null context should set irrespective of zero image study
      this.contextService.setContext(null);

      // Need to hide or close the viewer only if study data is available and viewer has initialized
      // if ($scope.viewerEnabled && studyAvailable) {
      if (this.integrationStatusService.isUvActive() && studyAvailable) {
        this.diConfigService.getJwtToken().then(jwtToken => {
          // Create the hide xml
          const launchXML = this.xmlTemplateManagerService.getViewerSetContextXml(jwtToken);
          log.debug('InvokeAPI : ' + studyId + ' ; interrupt previously opened viewer');
          this.viewerInvokeAPIWrapper(launchXML, '', '', 'UViewer');
        });
      }
    } else {
      // Preview study is same as currently launched study
      log.info('handlePreviewRequest event : ' + studyId + ' ; study is already open');
    }
  }

  handleCloseExamRequest(studyid, actionType?) {
    if (
      !this.diService.isSupportedBrowser() ||
      this.uvNotInitializedORNotActive() ||
      this.skipViewerCloseForInactiveTab(studyid)
    ) {
      return;
    }
    this.contextService.removeDocScanStatus(studyid);
    studyid = this.removePriorContextAndReturnStudyID(studyid);
    const studyDataAvailable = this.contextService.isStudyDataAvailable(studyid);
    // Close request for a different context is received
    if (this.handleCloseForDifferentContext(studyid, actionType)) {
      return;
    }

    // If study is present in context, close the viewer and report with discard option.
    if (this.contextService.checkStudyInContext(studyid)) {
      const studyContext = this.contextService.getContextItem(studyid);
      // is viewer been opened, but not acknowledged
      if (
        this.viewerMessageQueueParams.messageWaitingForResponse !== undefined &&
        this.viewerMessageQueueParams.messageWaitingForResponse.NCD === studyid
      ) {
        log.error('handleCloseExamRequest - Cannot close viewer that has not finished opening - ' + studyid);
        this.notify.warning(
          this.translate
            .instant('di.desktopIntegration.warning.viewerFailedToFinishOpening')
            .replace(/ACC#/g, this.contextService.getContextItem(studyid).accession),
          this.translate.instant('di.desktopIntegration.title')
        );
        this.contextService.removeContext(studyid);
        this.contextService.setContext(null);
        log.error('Viewer response pending, skip handleCloseExamRequest action');
        return;
      }

      this.contextService.removeContext(studyid);
      if (studyDataAvailable) {
        this.diConfigService.getJwtToken().then(jwtToken => {
          log.info(
            'handleCloseExamRequest - InvokeAPI event - viewer: ' + studyid + ' ; set context to' + ' close the viewer'
          );
          // Setting the save option as close.
          this.contextService.setCloseFlag(true);
          this.contextService.setContext(null);
          const launchXML = this.xmlTemplateManagerService.getViewerSetContextXml(jwtToken, actionType);
          const isSuccess = this.viewerInvokeAPIWrapper(launchXML, '', '', 'UViewer');

          if (!isSuccess) {
            this.messageService.broadcast('removeTab', studyid);
          }

          // Used in checking with the current context id in viewerCloseResponseHandler
          this.tabCloseId = studyid;
        });
      } else {
        let responseMsg = 'The ' + studyContext.studyDataAvailability + ' exam closed, operation completed';
        let responseCode = appConstants.genExCodes.ERR_SYSTEM_ERROR;

        if (studyContext.studyDataAvailability == 'OFFLINE') {
          responseMsg = 'The Offline exam closed, operation completed';
          responseCode = appConstants.genExCodes.ERR_STUDY_NOT_ONLINE;
        }
        this.externalAppContext.acknowledgeGExInbound(
          this.externalAppContext.getGenExResponseData(appConstants.SUCCESS, responseMsg, [responseCode]),
          false
        );
      }
    } else {
      log.debug('handleCloseExamRequest event : ' + studyid + ' ; Study not in context');
      this.externalAppContext.acknowledgeGExInbound(
        this.externalAppContext.getGenExResponseData(appConstants.SUCCESS, 'The operation completed successfully.', [
          appConstants.genExCodes.ERR_SYSTEM_ERROR
        ]),
        false
      );
    }
  }

  /**
   * Checks UV not initialized/active
   */
  uvNotInitializedORNotActive() {
    return !this.uvInitialized() || !this.integrationStatusService.isUvActive();
  }

  /**
   * Function checks the same viewer  is launched from active tab and inactive tab, whenever inactive tab
   * close request comes, checks the same viewer is launched in active tab primary/prior, if the same viewer
   * is launched for active, skip viewer close for inactive
   */
  skipViewerCloseForInactiveTab(studyid) {
    const activeTab = this.utilService.getActiveTab();
    // if active tab study id and close study id are different, means inactive tab close
    if (activeTab && activeTab.id !== studyid) {
      const inactiveTabPrior = this.contextService.getPriorContext(studyid);
      const activeTabPrior = this.contextService.getPriorContext(activeTab.id);
      // Checks active tab primary viewer with inactive tab prior viewer
      if (
        inactiveTabPrior &&
        this.contextService.isStudyDataAvailable(activeTab.id) &&
        activeTab.id === inactiveTabPrior.id
      ) {
        log.info(
          'skipViewerCloseForInactiveTab - active tab primary viewer matches with inactive ' +
            'tab prior viewer, active tab primary study id:' +
            activeTab.id +
            ', ' +
            'inactive tab prior study id' +
            inactiveTabPrior.id
        );
        // Remove inactive tab prior map entry
        this.contextService.removeFromPriorContextMap(studyid);
        return true;
      } else if (
        activeTabPrior &&
        this.contextService.isStudyDataAvailable(activeTabPrior.id) &&
        activeTabPrior.id === studyid
      ) {
        // Checks active tab prior viewer with inactive tab primary viewer
        log.info(
          'skipViewerCloseForInactiveTab - active tab prior viewer matches with inactive ' +
            'tab primary viewer, active tab prior study id:' +
            activeTabPrior.id +
            ', ' +
            'inactive tab primary study id' +
            studyid
        );
        return true;
      } else {
        return false;
      }
    }
    return false;
  }

  /**
   * Function removes prior context from map and returns prior context study id
   *
   */
  removePriorContextAndReturnStudyID(studyid) {
    const comparisonContext = this.contextService.getPriorContext(studyid);
    if (comparisonContext) {
      this.contextService.removeFromPriorContextMap(studyid);
      studyid = comparisonContext.id;
    }
    return studyid;
  }

  /**
   * This method will save and close the viewer.
   *
   * The responsibility of this method is to:
   *  1. Send SETCONTEXT for ViewerPlugin to close viewer which is out of context.
   *
   */
  saveAndCloseViewer(contextId) {
    const comparisonContext = this.contextService.getPriorContext(contextId);
    if (comparisonContext) {
      this.contextService.removeFromPriorContextMap(contextId);
      contextId = comparisonContext.id;
    }
    // Getting requested procedure ID as parameter of this event. Use that to close matching viewer.
    const currentContext = this.contextService.getCurrentContext();
    if (contextId === currentContext) {
      // is viewer been opened, but not acknowledged
      if (
        this.viewerMessageQueueParams.messageWaitingForResponse !== undefined &&
        this.viewerMessageQueueParams.messageWaitingForResponse.NCD === contextId
      ) {
        log.error('saveAndCloseViewer - Cannot close viewer that has not finished opening - ' + contextId);
        this.notify.warning(
          this.translate
            .instant('di.desktopIntegration.warning.viewerFailedToFinishOpening')
            .replace(/ACC#/g, this.contextService.getContextItem(contextId).accession),
          this.translate.instant('di.desktopIntegration.title')
        );
        this.contextService.removeContext(currentContext);
        this.contextService.setContext(null);
        return;
      }
      this.diConfigService.getJwtToken().then(jwtToken => {
        const studyDataAvailable =
          this.contextService.isStudyDataAvailable(contextId) &&
          this.integrationStatusService.isUvActive() &&
          this.uvInitialized();
        // Close viewer corresponding to the report which is saved
        this.contextService.removeContext(contextId);
        if (studyDataAvailable) {
          let studyId = this.contextService.getCurrentContext() || '';
          if (contextId === this.contextService.getCurrentContext()) {
            studyId = '';
            this.contextService.setContext(null);
          }
          const launchXML = this.xmlTemplateManagerService.getViewerSetContextXml(jwtToken);
          log.info('saveAndCloseViewer - InvokeAPI - close  viewer');
          this.viewerInvokeAPIWrapper(launchXML, studyId, '', 'UViewer');

          this.tabCloseId = contextId;
        }
      });
    } else {
      log.info(
        'saveAndCloseViewer - currentContext not equal to new context - param contextid = ' +
          contextId +
          ' currentContext = ' +
          currentContext
      );
    }
  }

  /**
   * Function to close viewer either save or discard option
   */
  closeViewer(actionName, contextId?) {
    log.debug('closeViewer context Id : ' + contextId + ', actionName : ' + actionName);
    // If viewer is still loading images no need to perform action.
    if (
      this.viewerMessageQueueParams.messageWaitingForResponse !== undefined &&
      this.viewerMessageQueueParams.messageWaitingForResponse.NCD === contextId
    ) {
      log.error('closeViewer - Cannot perform action because viewer has not finished opening - ' + contextId);
      this.notify.warning(
        this.translate
          .instant('di.desktopIntegration.warning.viewerFailedToFinishOpening')
          .replace(/ACC#/g, this.contextService.getContextItem(contextId).accession),
        this.translate.instant('di.desktopIntegration.title')
      );
      return false;
    }

    if (actionName === 'closediscard') {
      log.debug('closeViewer, discard and close viewer');
      // Discard and close viewer
      this.handleCloseExamRequest(contextId);
    } else {
      log.debug('closeViewer, save and close viewer');
      // Save and close viewer
      this.saveAndCloseViewer(contextId);
    }
    return true;
  }

  /**
   * This method sends viewer plugin SETCONTEXT to LineShaft to clear cache and deletes viewer corresponding to the
   * viewer closed.
   * The responsibility of this method is to:
   *  1. Send SETCONTEXT for ViewerPlugin to close viewer which is out of context.
   *
   */
  viewerSavedEventHandler(contextId) {
    // Getting requested procedure ID as parameter of this event
    // Use that to close matching viewer
    const currentContext = this.contextService.getCurrentContext();
    if (contextId === currentContext) {
      // Close viewer corresponding to the report which is saved
      this.contextService.removeContext(currentContext);
      this.contextService.setContext(null);
      this.diConfigService.getJwtToken().then(jwtToken => {
        const launchXML = this.xmlTemplateManagerService.getViewerSetContextXml(jwtToken);
        log.info('viewerSavedEventHandler - InvokeAPI - close  viewer');
        this.viewerInvokeAPIWrapper(launchXML, '', 'UViewer', undefined);
      });
    }
  }

  checkViewerExistAndClosable(contextId) {
    let isViewerExist = false;
    const priorContext = this.contextService.getPriorContext(contextId);
    contextId = priorContext ? priorContext.id : contextId;
    // To identify 0 image study
    const studyDataAvailable = this.contextService.isStudyDataAvailable(contextId);
    // If exam is in context, viewer integration is enabled and exam has images set call back to perform action.
    if (
      this.contextService.checkStudyInContext(contextId) &&
      this.uvInitialized() &&
      this.integrationStatusService.isUvActive() &&
      studyDataAvailable
    ) {
      isViewerExist = true;
    }
    log.debug('checkViewerExistAndClosable context Id : ' + contextId + ', isViewerExist : ' + isViewerExist);
    return isViewerExist;
  }

  checkViewerVisibleForStudy(contextId) {
    return contextId === this.contextService.getCurrentContext() && this.integrationStatusService.getUVLaunchStatus();
  }

  skipUViewerLaunch(context, workItem, prevContext, previousNullContext, prevStudyAvailable, jwtToken) {
    // Skip uViewer Launch for Unsigned Worklist && external tab when activating exam!
    if (workItem.disableAutoLaunchViewer) {
      if (prevContext !== this.contextService.getCurrentContext()) {
        this.hideViewerForPrevContextAndStudy(previousNullContext, prevStudyAvailable, jwtToken);
      }
      log.info('handleLaunchRequest:  Launching of Viewer is Skipped for Unsigned Worklist!.');
      return true;
    }

    // do not return from parent
    return false;
  }

  checkandAbortViewerLaunch(context, readOnlyFlag) {
    if (context.keys.source === 'desktop-integration' || !this.diService.isSupportedBrowser()) {
      return true;
    }
    if (this.diService.getApplicationContext().toString() !== context.id.toString()) {
      log.warn(
        'read action handler - context (' +
          context.id +
          ') does not match with active' +
          ' work item (' +
          this.diService.getApplicationContext() +
          '), ignore launch' +
          (readOnlyFlag ? '-readonly' : '') +
          ' request'
      );
      return true;
    }

    if (!this.uvInitialized()) {
      log.error('handleLaunchRequest - viewer initialization in progress');
      return true;
    }
    if (!this.integrationStatusService.isUvActive()) {
      log.info('handleLaunchRequest - viewer is disabled');
      return true;
    }
    return false;
  }

  handleCloseForDifferentContext(studyid, actionType) {
    const studyDataAvailable = this.contextService.isStudyDataAvailable(studyid);
    if (this.contextService.checkStudyInContext(studyid) && studyid !== this.contextService.getCurrentContext()) {
      log.info('handleCloseExamRequest - close request for a hidden context is received studyid : ' + studyid);
      const viewerOpened = this.contextService.isViewerOpened(studyid);
      const studyContext = this.contextService.getContextItem(studyid);
      this.contextService.removeContext(studyid);
      if (studyDataAvailable && viewerOpened) {
        // Take the current context based on the study data availability to compare the context while receiving
        // the response.
        let currentContext = this.contextService.getCurrentContext();
        currentContext =
          currentContext !== null && this.contextService.isStudyDataAvailable(currentContext) ? currentContext : '';
        //If active tab initiated by closeAllTab or selectedCloseTab no need to invoke viewerInvokeAPIWrapper
        const lastTab = this.utilService.getTab(currentContext);
        if (!lastTab || (lastTab && !lastTab.workItem.isCloseingTab)) {
          this.diConfigService.getJwtToken().then(jwtToken => {
            log.info(
              'handleCloseExamRequest - sending viewer launch/hide xml with current context to close the hidden viewer'
            );
            this.contextService.setCloseFlag(true);
            // Sending the viewer launch/hide xml based on current context to close the hidden viewer
            const reLaunchXML = this.xmlTemplateManagerService.getViewerSetContextXml(jwtToken, actionType);
            // Parameter to identify inactive tab close to skip the current context checking inside
            // viewerInvokeAPIWrapper method
            const hiddenContextClose = true;
            this.viewerInvokeAPIWrapper(reLaunchXML, currentContext, '', 'UViewer', hiddenContextClose);
          });
        }
        // Used in checking with the current context id in viewerCloseResponseHandler
        this.tabCloseId = studyid;
      } else if (studyContext && studyContext.imageCount == 0) {
        this.externalAppContext.acknowledgeGExInbound(
          this.externalAppContext.getGenExResponseData(
            appConstants.SUCCESS,
            'Zero images exam closed, operation completed',
            [appConstants.genExCodes.ERR_ZERO_IMAGE_STUDY]
          ),
          false
        );
      }

      // exit parent
      return true;
    } else if (!this.contextService.checkStudyInContext(studyid)) {
      log.debug('handleCloseForDifferentContext event : ' + studyid + ' ; Study not in context');
      this.externalAppContext.acknowledgeGExInbound(
        this.externalAppContext.getGenExResponseData(appConstants.SUCCESS, 'The operation completed successfully.', [
          appConstants.genExCodes.ERR_SYSTEM_ERROR
        ]),
        false
      );
    }

    // do not exit parent
    return false;
  }

  /**
   * Method to handle the VIEWER_ERROR response.
   *
   * responseInfo - response info object
   */
  ViewerErrorResponseHandler(responseInfo) {
    if (responseInfo.topicName === 'RadDesktop.VIEWER_ERROR') {
      log.error(
        'ViewerErrorResponseHandler - VIEWER_ERROR response received with error code : ' + responseInfo.errorCode
      );
    }
  }

  docScanResponseHandler(responseInfo) {
    if (responseInfo.topicName === 'LS_DOC_SCAN_STATUS') {
      log.info('docScanResponseHandler - DocScan status : ' + responseInfo.docScanStatus);
      const responseData = responseInfo.docScanStatus.split('|');
      const str = JSON.stringify(responseData[0]);
      const lastIndexOf = str.lastIndexOf(':');
      const examId = parseInt(str.slice(lastIndexOf + 1), 10);
      const scanStatus = responseData[1];
      this.contextService.setDocScanStatus(examId, scanStatus);
      if (scanStatus === 'DOC_SCAN_COMPLETED') {
        this.notify.success(this.translate.instant('di.desktopIntegration.docScanSuccess'));
        this.auditService.saveAuditAction(
          this.auditService.getActionCode().Execute,
          this.auditService.getActionType().ScanDocumentsCompleted,
          this.auditService.getEventOutcome().Success
        );
      } else if (scanStatus === 'DOC_SCAN_ERROR') {
        this.notify.warning(this.translate.instant('di.desktopIntegration.docScanError'));
        this.auditService.saveAuditAction(
          this.auditService.getActionCode().Execute,
          this.auditService.getActionType().ScanDocumentsFailure,
          this.auditService.getEventOutcome().MinorFailure
        );
      }
    }
  }
  /**
   * Method to handle the invalid session error.
   *
   * responseInfo - response info object
   */
  handleInvalidSessionError(responseInfo) {
    log.info('handleInvalidSessionError: invalid session error');
    let decision = null;
    if (this.integrationStatusService.isUvActive()) {
      if (responseInfo.topicName === 'RadDesktop.VIEWER_ERROR') {
        this.clearMessageQueueAndSendCloseXML();
        decision = this.notify.critical(
          this.translate.instant('di.desktopIntegration.warning.viewerSessionRecreate'),
          this.translate.instant('di.desktopIntegration.titleViewerRenewed')
        );
        if (decision) {
          decision.then(() => {
            log.info('handleInvalidSessionError - session values from cache');
          });
        }
      } else {
        this.notify.critical(
          this.translate.instant('di.desktopIntegration.critical.loadViewerFailed'),
          this.translate.instant('di.desktopIntegration.titleViewerDisabled')
        );
        this.integrationStatusService.setViewerHealthStatus(
          this.integrationStatusService.healthStatusEnum.failed,
          true
        );
        log.info('handleInvalidSessionError - session values from cache');
        this.executeTabAction();
      }
    }
  }

  executeTabAction() {
    const actionTriggerTime = 1000;
    const uvLaunchTimeout = setTimeout(() => {
      const tab = this.utilService.getActiveTab();
      tab.workItem.keys.openViewer = true;

      this.eventService.sendEvent(new EventMessage(Events[tab.examContext], { workItem: tab.workItem })).then(
        () => {
          log.info('handleInvalidSessionError -' + tab.examContext + 'success');
        },
        () => {
          log.error('handleInvalidSessionError -' + tab.examContext + 'failure');
        }
      );
      clearTimeout(uvLaunchTimeout);
    }, actionTriggerTime);
  }

  /**
   * This method clears the viewer message queue, remove all contexts and send close xml.
   */
  clearMessageQueueAndSendCloseXML() {
    this.viewerMessageQueueParams.messageQueue = [];
    // Removing the viewer contexts
    this.contextService.removeAllContext();
    // Close all the launched viewers.
    this.diConfigService.getJwtToken().then(jwtToken => {
      const launchXML = this.xmlTemplateManagerService.getViewerSetContextXml(jwtToken);
      this.diService.invokeAPIWrapperExecute(launchXML);
    });
  }

  /**
   * This method will hide all the opened viewers.
   */
  hideAllOpenViewers() {
    this.diConfigService.getJwtToken().then(jwtToken => {
      // Create the hide xml
      this.contextService.setContext(null);
      const hideXML = this.xmlTemplateManagerService.getViewerSetContextXml(jwtToken);
      log.debug('hideAllOpenViewers : hide all the opened viewers');
      if (this.viewerInvokeAPIWrapper(hideXML, '', '', 'UViewer')) {
        this.integrationStatusService.setViewerHealthStatus(
          this.integrationStatusService.healthStatusEnum.hideAll,
          true
        );
      }
    });
  }

  // Display warning message if viewer is initializing.
  uvInitialized() {
    if (this.integrationStatusService.getUViewerPluginStatus()) {
      return true;
    }
    if (this.integrationStatusService.isUvActive()) {
      log.info('Viewer initialization is in progress');
      this.notify.warning(
        this.translate.instant('di.desktopIntegration.warning.viewerIsInitializing'),
        this.translate.instant('di.desktopIntegration.title')
      );
    }
    return false;
  }

  hideViewerForPrevContextAndStudy(previousNullContext, prevStudyAvailable, jwtToken) {
    PerformanceMonitorService.clear(PerformanceSource.VIEWER);
    // Hide the viewer only if previous context is present and study is available
    if (!previousNullContext && prevStudyAvailable) {
      // Resetting the context to null for creating the hide xml.
      this.contextService.setContext(null);
      const hideXML = this.xmlTemplateManagerService.getViewerSetContextXml(jwtToken);
      this.viewerInvokeAPIWrapper(hideXML, '', '', 'UViewer');
    }
  }

  viewerInvokeAPIWrapper(xml, studyId, accession, viewerIdentifier, hiddenContextClose?) {
    if (studyId === '' && _.isEmpty(this.contextService.getViewerContext()) && !hiddenContextClose) {
      log.info('viewerInvokeAPIWrapper - skip viewer hide or close since there is no open viewer');
      return false;
    }
    const lsMessage = this.diService.getLSMessage(this.diService.getLSMessageType().SETCONTEXT, xml);
    lsMessage.NCD = studyId;
    lsMessage.accession = accession;
    lsMessage.viewerIdentifier = viewerIdentifier;
    this.integrationStatusService.setViewerHealthStatus(
      this.integrationStatusService.healthStatusEnum.pendingResponse,
      true
    );
    // LoadMessageQueueParams.messageWaitingForResponse is checked to process the setcontext requests only after
    // completing the load requests in queue.
    if (
      this.viewerMessageQueueParams.messageWaitingForResponse === undefined &&
      this.loadMessageQueueParams.messageWaitingForResponse === undefined
    ) {
      log.info('calling invokeAPIWrapperExecute from viewerInvokeAPIWrapper');
      // Reset the viewerRequestRetryCount on sending the request to ViewerPlugin
      this.viewerRequestRetryCount = 0;
      this.viewerMessageQueueParams.messageWaitingForResponse = lsMessage;
      this.diService.invokeAPIWrapperExecute(lsMessage.message);
      this.setViewerSetContextTimeout();
    } else {
      // put on queue
      this.viewerMessageQueueParams.messageQueue.push(lsMessage);
      log.info(
        'viewerInvokeAPIWrapper - Add to queue - queue count: ' + this.viewerMessageQueueParams.messageQueue.length
      );
    }
    return true;
  }

  isRemoteExam(context) {
    return UtilService.isRemoteExam(context);
  }

  createUViewerIntegrationContext(context, readOnlyFlag) {
    // If viewer type is iSite, no need to launch universal viewer so setting
    // the study data availability to OFFLINE.
    let studyAvailability;
    if (!!context.keys.archiveAdoptedStatus && context.keys.archiveAdoptedStatus !== 'NO') {
      studyAvailability = 'OFFLINE';
    } else {
      // Set studyAvailability to 'ONLINE' if study is from Remote Archive, keep original value if from Local.
      studyAvailability = this.isRemoteExam(context) ? 'ONLINE' : context.keys.studyDataAvailability;
    }
    // No need to set readonly flag if an exam is launched from unsigned worklist with viewer preference OFF and
    // user clicks on View Images button to launch viewer
    const examContextFromTabList = this.getExamContextFromTabList(context.id);
    const isReadOnlyExam =
      examContextFromTabList === undefined
        ? readOnlyFlag
        : examContextFromTabList === 'read' && readOnlyFlag
        ? false
        : readOnlyFlag;
    const workItem = {
      archiveAdoptedStatus: context.keys.archiveAdoptedStatus,
      requestedProcedureId: context.id,
      procedureAccessionNo: context.keys.procedureAccessionNo,
      studyDataAvailability: studyAvailability,
      procedureStatusId: context.keys.procedureStatusId,
      readOnlyExam: isReadOnlyExam,
      mrn: context.keys.mrn,
      patientName: context.keys.patientName,
      patientNameObj: context.keys.patientNameObj,
      DOB: context.keys.patientDoB,
      modality: context.keys.modality,
      patientGender: context.keys.patientGender,
      repoStudyUID: context.keys.procedureStudyInstanceUID,
      repositoryType: context.keys.repositoryType,
      repositoryId: context.keys.repositoryId,
      imageCount: context.keys.imageCount,
      rejectedImageCount: context.keys.rejectedImageCount,
      studyClickTime: context.keys.studyClickTime,
      // FALSE -  Exam is not from unsigned worklist/ from unsigned worklist but preference is ON/
      //          from unsigned worklist, preference OFF but viewer launched via ViewImages button.
      // TRUE  -  from unsigned worklist and viewer preference is OFF
      disableAutoLaunchViewer: context.keys.disableAutoLaunchViewer,
      protocolWorklist: context.keys.protocolWorklist,
      isGlobalExam: UtilService.isGlobalExam(context),
      siteId: context.keys.siteId
    };
    return workItem;
  }

  getExamContextFromTabList(requestedProcId) {
    let examContext;
    _.each(this.tabs, (value: any) => {
      if (examContext === undefined && value.id === requestedProcId) {
        examContext = value.examContext;
      }
    });
    return examContext;
  }

  /**
   * Check viewer launch response is pending
   *
   * actionName - action name
   * studyId - study id
   *
   * Return : true if response is pending, else false
   */
  checkViewerContext(actionName, studyId) {
    const reopenParams = {
      source: 'desktop-integration',
      reason: 'reopen'
    };
    // Check viewer hide or close response is pending
    if (this.viewerHideOrCloseResponsePending()) {
      this.integrationStatusService.setViewerHealthStatus(
        this.integrationStatusService.healthStatusEnum.pendingResponse,
        true
      );
      this.notify.warning(
        this.translate.instant('di.desktopIntegration.warning.viewerHideOrCloseResponsePending'),
        this.translate.instant('di.desktopIntegration.title')
      );
      log.warn(this.translate.instant('di.desktopIntegration.warning.viewerHideOrCloseResponsePending'));
      return true;
    }

    let accession = this.contextService.getStudyIdentifierOfGivenContext(studyId);
    log.debug(
      'checkViewerContext : before action ' +
        actionName +
        ' - requestedProcedureId is ' +
        studyId +
        ' and accession is ' +
        (accession || 'unknown')
    );

    // Saving the current work item id on tab close
    // This tabCloseId will be used later in ViewerCloseResponseHandler() to compare with
    // current context id for Viewer close
    if (actionName === 'closediscard') {
      this.tabCloseId = studyId;
    }

    if (this.testViewerStuck(actionName, accession, studyId)) {
      this.integrationStatusService.setViewerHealthStatus(
        this.integrationStatusService.healthStatusEnum.lastActionFailed,
        true
      );
      studyId = this.viewerMessageQueueParams.messageWaitingForResponse?.NCD;
      accession = this.viewerMessageQueueParams.messageWaitingForResponse?.accession;

      const stuckId = studyId ? studyId : this.contextService.getContextCorrespondingToAccessionNum(accession);
      const contextItem = this.contextService.getContextItem(studyId);
      log.info(
        'checkViewerContext : before action ' +
          actionName +
          ' - viewer is stuck for accession ' +
          (accession || (contextItem && contextItem.accession))
      );

      this.checkAndExecuteActionForStuckId(stuckId, reopenParams);
      this.notify.warning(
        this.translate
          .instant('di.desktopIntegration.warning.viewerFailedToFinishOpening')
          .replace(/ACC#/g, accession || contextItem?.accession),
        this.translate.instant('di.desktopIntegration.title')
      );
      return true;
    }
    return false;
  }

  checkAndExecuteActionForStuckId(stuckId, reopenParams) {
    if (stuckId) {
      const actionForStuckId = this.contextService.isReadOnlyExam(stuckId) ? 'launchreadonly' : 'read';
      this.eventService.sendEvent(new EventMessage(Events[actionForStuckId], new EventWorkItem(stuckId, reopenParams)));
    }
  }

  /**
   * close hidden viewers during log out.
   * un load viewer plugin
   */
  cleanup = (): boolean => {
    try {
      log.info('Cleanup - Started');
      // During logout LineShaft WebSocket connection is closed without any delay. So even if we send the
      // Close all Open Viewer request form here it may not be processed by the Plugin. So removing the Close all
      // Open Viewer request from here, it is already handled in Plugin Close handler which is supposed to
      // execute either from the Close request sending below or from LineShaft WebSocket connection close flow.

      // ViewerPlugin close xml should send irrespective of load success/failure
      // Create the close xml and close viewer plugin
      const viewerCloseXML = this.xmlTemplateManagerService.getPluginCloseXml(
        this.diConfigService.UVIEWER_CONFIG.MODULENAME
      );
      this.diService.invokeAPIWrapperExecute(viewerCloseXML);
      return true;
    } catch (error) {
      log.error('Cleanup - Error >> ', error);
      return false;
    }
  };

  /**
   * Method to handle the Viewer response.
   * response - response object
   */
  handleViewerResponse(response) {
    if (response.Action === 'Update' && response.Result === 'Error') {
      log.error('handleViewerResponse - Viewer update failed, disabling the Viewer integration');
      this.integrationStatusService.setUViewerPluginStatus(true);
      this.integrationStatusService.setViewerHealthStatus(this.integrationStatusService.healthStatusEnum.failed, true);
      this.notify.critical(
        this.translate.instant('di.desktopIntegration.critical.loadViewerFailed'),
        this.translate.instant('di.desktopIntegration.titleViewerDisabled')
      );
    }
    if (response.LineShaft === 'Terminated') {
      log.error('handleViewerResponse - LineShaft terminated as part of plugin upgrade');
      this.notify.critical(
        this.translate.instant('di.desktopIntegration.updated'),
        this.translate.instant('di.desktopIntegration.title')
      );
    }
  }

  /**
   * Method to handle Viewer plugin load response.
   */
  handleViewerPluginLoadResponse(responseInfo) {
    log.info(' Viewer plugin has initialized');
    if (responseInfo.resultValue === 'SUCCESS') {
      if (this.integrationStatusService.getUViewerPluginStatus()) {
        log.info('handleViewerPluginLoadResponse : viewer plugin load success after timeout');
        this.notify.success(
          this.translate.instant('di.desktopIntegration.success.loadViewerSuccessAfterTimeout'),
          this.translate.instant('di.desktopIntegration.titleViewerEnabled')
        );
        // Reset viewer health status to update the status from failed to ready.
        this.integrationStatusService.resetUViewerHealthStatus();
      }
      log.info('handleViewerPluginLoadResponse : viewer plugin load success');
      this.integrationStatusService.setUViewerPluginStatus(true);
      this.integrationStatusService.setViewerHealthStatus(this.integrationStatusService.healthStatusEnum.ready, true);
    } else {
      log.error(
        'handleViewerPluginLoadResponse - viewer plugin load failed - Failure reason : ' + responseInfo.resultMsg
      );
      this.integrationStatusService.setUViewerPluginStatus(true);
      this.integrationStatusService.setViewerHealthStatus(this.integrationStatusService.healthStatusEnum.failed, true);
    }
  }

  uvBehavesLikeReportSaved(contextId) {
    if (
      this.diConfigService.UVIEWER_CONFIG.UVCLOSE_ACTS_LIKE_REPORT_SAVED === 'true' &&
      !this.contextService.isReadOnlyExam(contextId)
    ) {
      log.info(
        'ViewerCloseResponseHandler - Close  Viewer - ' +
          'uviewer.viewerclosed.actslike.reportsaved =' +
          this.diConfigService.UVIEWER_CONFIG.UVCLOSE_ACTS_LIKE_REPORT_SAVED
      );
      this.contextService.removeContext(contextId);
      this.performAction('closeviewer', new EventWorkItem(contextId, { source: '' }));
    }
  }

  removePriorContextAndOverlay(contextId) {
    const activeTab = this.utilService.getActiveTab();

    if (activeTab?.viewAsPrimaryId) {
      this.integrationStatusService.setUVLaunchStatus(false);
      this.contextService.removeFromPriorContextMap(activeTab.id);
      this.contextService.removeContext(contextId);
      activeTab.viewAsPrimaryId = undefined;
      this.messageService.broadcast('closeViewImagePriors', contextId);
    }
  }

  closeTabOnUVClose(responseInfo) {
    const uvTabCloseId = this.extractStudyId(responseInfo.examIdent);
    const isUVCrashed = responseInfo.isUVCrashed;
    // Clearing context to avoid UV flashing when closed by cicking on done door/close trigger.
    this.contextService.setViewerContext('');
    if (isUVCrashed) {
      log.error('closeTabOnUVClose - Close Viewer - ' + responseInfo.topicName + 'Viewer crashed');
      this.contextService.removeContext(uvTabCloseId);
    } else if (uvTabCloseId !== undefined) {
      this.messageService.broadcast('lineShaft.viewerClosed', uvTabCloseId);
    }
  }

  /**
   * Method to extract the study id from exam identifier.
   *
   * examIdentifier - exam identifier
   */
  extractStudyId(examIdentifier) {
    let studyId;
    if (examIdentifier !== undefined && examIdentifier != null && examIdentifier.indexOf(':') > -1) {
      const examId = examIdentifier.split(':')[1];
      if (isNaN(examId)) {
        studyId = examId;
      } else {
        studyId = parseInt(examId, 10);
      }
    }
    return studyId;
  }

  /**
   * Method to handle viewer closed response.
   * responseInfo - response info object
   */
  ViewerCloseResponseHandler(responseInfo) {
    if (responseInfo.topicName === 'RadDesktop.LS_VIEWER_CLOSED' && responseInfo.sourceName === 'ViewerPlugin') {
      log.info('ViewerCloseResponseHandler - Close  Viewer - ' + responseInfo.topicName);
      if (this.checkForContextChangeClose(responseInfo)) {
        log.info('Skipping viewer closed response since it is terminated by LineShaft on context change');
        return;
      }
      const contextId = this.contextService.getCurrentContext();

      if (!contextId) {
        const uvTabCloseId = this.extractStudyId(responseInfo.examIdent);
        this.contextService.removeContext(uvTabCloseId);
        return;
      }

      if (this.tabCloseId === undefined || this.tabCloseId === contextId) {
        // Reset the viewer context only for the active viewer close using done option
        this.contextService.setViewerContext('');
        this.uvBehavesLikeReportSaved(contextId);
        this.tabCloseId = undefined;
        // Remove the prior context and overlay in the imaging history when the viewer is closed manually
        this.removePriorContextAndOverlay(contextId);
      }

      if (this.diConfigService.UVIEWER_CONFIG.UVCLOSE_ACTS_LIKE_REPORT_SAVED === 'false') {
        this.closeTabOnUVClose(responseInfo);
      }
      // Resetting the uv enabled flag if the viewer is closed for the current context
      // This flag is used for the Unsigned worklist launch viewer preference
      const viewerCloseId = this.extractStudyId(responseInfo.examIdent);
      if (viewerCloseId === contextId) {
        this.utilService.setUvEnabled(false);
      }
      this.integrationStatusService.setUVLaunchStatus(false);
    }
  }

  /**
   * Method to check whether viewer close is due to context change.
   *
   * responseInfo - response info object
   */
  checkForContextChangeClose(responseInfo) {
    let bContextChange = false;
    if (responseInfo.closedReason === 'CONTEXT_CHANGE_CLOSE') {
      log.info('checkForContextChangeClose - Viewer closed due to context change');
      bContextChange = true;
    } else if (responseInfo.closedReason === 'CONTEXT_CHANGE_CLOSEALL') {
      if (this.utilService.getTabsLength() > 0) {
        log.error('checkForContextChangeClose - Closing out all the tabs to prevent out of context scenarios');
        this.contextService.removeAllContext();
        this.messageService.broadcast('closeAllTabs');
        this.messageService.broadcast('ContextMismatch.TabClose', true);
      }
      bContextChange = true;
    }
    return bContextChange;
  }

  processQueueOnSetcontextResponse(responseInfo) {
    const studyIdFromResponse = responseInfo.currentIndex !== '-1' ? this.getExamID(responseInfo.examIdent) : '';
    if (
      this.viewerMessageQueueParams.messageWaitingForResponse &&
      this.viewerMessageQueueParams.messageWaitingForResponse.NCD === studyIdFromResponse
    ) {
      log.info('processQueueOnSetcontextResponse - process next request from viewer queue');
      this.viewerMessageQueueParams.messageWaitingForResponse = undefined;
      // Clear the viewer setcontext timeout
      clearTimeout(this.viewerSetContextTimeout);
      this.processViewerQueue();
    } else {
      if (_.isUndefined(this.viewerMessageQueueParams.messageWaitingForResponse)) {
        return;
      }
      log.warn(
        'Unexpected response received from Viewer- Expected NCD:' +
          this.viewerMessageQueueParams.messageWaitingForResponse.NCD +
          ' Received:' +
          studyIdFromResponse
      );
    }
  }

  private getExamID(id) {
    if (_.isFinite(id)) {
      id = parseInt(id, 10);
    }
    return id;
  }

  /**
   * Method to handle viewer plugin setcontext response.
   */
  handleViewerPluginSetcontextResponse(responseInfo) {
    const isContextMismatch =
      responseInfo.currentIndex !== '-1' &&
      !responseInfo.isDiscardXML &&
      this.checkAndHandleContextMismatch(responseInfo);

    // Check the response is of open viewer not hide/close viewer.
    // If the received response is of discard viewer no need to hide viewer and report.
    if (isContextMismatch) {
      this.externalAppContext.acknowledgeGExInbound(responseInfo, isContextMismatch);
      return;
    }
    let isContextRemove = true;

    if (responseInfo.resultValue === 'FAILURE') {
      this.utilService.setUvEnabled(false);
      this.integrationStatusService.setUVLaunchStatus(false);
      log.error(
        'handleViewerPluginSetcontextResponse - Launch Viewer Failed - Study ID: ' +
          responseInfo.examIdent +
          '; Failure Reason: ' +
          responseInfo.resultMsg
      );

      // Iterate through  failure types to display relevant error message
      if (_.contains(responseInfo.failureTypes, 'ERR_VERSIONMISMATCH')) {
        // In this case viewer integration is disabled
        this.handleViewerVersionMismatchResponse();
      } else if (_.contains(responseInfo.failureTypes, 'ERR_SESSIONINVALID')) {
        log.info('handleViewerPluginSetcontextResponse - SetContext failed with error code -' + ' ERR_SESSIONINVALID');
        this.handleInvalidSessionError(responseInfo);
      } else if (_.contains(responseInfo.failureTypes, 'ERR_STUDY_NOT_FOUND')) {
        log.error(
          'handleViewerPluginSetcontextResponse - SetContext failed with error code - ' +
            'ERR_STUDY_NOT_FOUND. Check the required services in Viewer server are running.'
        );
        this.notify.warning(
          this.translate.instant('di.desktopIntegration.warning.viewerSessionLost'),
          this.translate.instant('di.desktopIntegration.title')
        );
        this.integrationStatusService.setViewerHealthStatus(
          this.integrationStatusService.healthStatusEnum.lastActionFailed,
          true
        );
        this.hideAllOpenViewers();
      } else if (this.retryContextSwitchFailure(responseInfo)) {
        log.error('handleViewerPluginSetcontextResponse - CONTEXT_SWITCH_FAILURE : ' + this.viewerRequestRetryCount);
        this.handleViewerContextswitchFailureResponse(responseInfo);
        isContextRemove = this.viewerRequestRetryCount >= 3;
      } else {
        log.error('handleViewerPluginSetcontextResponse - Launch Viewer Failed, handling general' + ' error cases.');
        this.notify.error(
          this.translate.instant('di.desktopIntegration.error.viewerLaunchFailed') + responseInfo.resultMsg
        );
        this.integrationStatusService.setViewerHealthStatus(
          this.integrationStatusService.healthStatusEnum.lastActionFailed,
          true
        );
        this.hideAllOpenViewers();
      }
      this.handleContextRemove(isContextRemove, responseInfo);
      if (isContextRemove) {
        // Reset the viewerRequestRetryCount after finishing the retries
        this.viewerRequestRetryCount = 0;
        this.externalAppContext.acknowledgeGExInbound(responseInfo, isContextMismatch);
      }
    } else {
      this.externalAppContext.acknowledgeGExInbound(responseInfo, isContextMismatch);
      this.handleViewerSuccessSetcontextResponse(responseInfo);
    }
  }

  /**
   * Method checks to remove failed viewer contexts
   */
  handleContextRemove(flag, responseInfo) {
    if (flag) {
      this.contextService.removeContext(UtilService.parseInt(responseInfo.examIdent));
    }
  }

  /**
   * Method checks context mismatch and hide all opened viewers
   */
  checkAndHandleContextMismatch(responseInfo) {
    const dxHubContext = this.diService.getApplicationContext();
    const comparisonContext = this.contextService.getPriorContext(dxHubContext);
    // Setting viewer context on viewer success response to send hide xml in out of context scenario.
    // If viewer context is empty hide xml won't send from viewerInvokeAPIWrapper method.
    if (responseInfo.resultValue === 'SUCCESS') {
      this.contextService.setViewerContext(responseInfo.examIdent);
    }
    if (dxHubContext !== this.getExamID(responseInfo.examIdent) && !comparisonContext) {
      log.warn(
        'handleViewerPluginSetcontextResponse - Trying to clear all desktop integration ' +
          'context on context mismatch.'
      );
      // Hide all open viewers and report on context mismatch
      this.hideAllOpenViewers();
      return true;
    } else if (comparisonContext && comparisonContext.id !== this.getExamID(responseInfo.examIdent)) {
      this.hideAllOpenViewers();
      return true;
    }
    return false;
  }

  retryContextSwitchFailure(responseInfo) {
    return (
      _.contains(responseInfo.failureTypes, 'CONTEXT_SWITCH_FAILURE') &&
      !(
        _.contains(responseInfo.failureTypes, 'ERR_STUDY_NOT_ONLINE') ||
        _.contains(responseInfo.failureTypes, 'ERR_STUDY_BEING_RETRIEVED_ONLINE')
      )
    );
  }

  /**
   * Method to handle viewer plugin success setcontext response.
   */
  handleViewerSuccessSetcontextResponse(responseInfo) {
    // Reset the viewerRequestRetryCount while receiving the success response
    this.viewerRequestRetryCount = 0;
    if (responseInfo.currentIndex === '-1') {
      log.info('handleViewerSuccessSetcontextResponse - hide/close viewer response');
      this.contextService.setViewerContext('');
      this.triggerProcessQueueOnResponse(responseInfo);
      this.integrationStatusService.setViewerHealthStatus(this.integrationStatusService.healthStatusEnum.ready, true);
      this.messageService.broadcast('viewerClosed');
      this.utilService.setUvEnabled(false);
      this.integrationStatusService.setUVLaunchStatus(false);
    } else {
      this.messageService.broadcast('viewerLaunched', responseInfo.examIdent);
      PerformanceMonitorService.stop(PerformanceSource.VIEWER, this.contextService.getCurrentContext());
      log.info(
        'handleViewerSuccessSetcontextResponse -Open Viewer Success - Id: ' +
          responseInfo.examIdent +
          'currentIndex: ' +
          responseInfo.currentIndex
      );
      log.info(
        'handleViewerSuccessSetcontextResponse - getCurrentContext : ' + this.contextService.getCurrentContext()
      );
      const contextItem = this.contextService.getCurrentContextItem();
      if (contextItem) {
        contextItem.isViewerOpened = true;
      }
      this.contextService.setViewerContext(responseInfo.examIdent);
      this.integrationStatusService.setViewerHealthStatus(this.integrationStatusService.healthStatusEnum.ready, true);
      this.utilService.setUvEnabled(true);
      this.integrationStatusService.setUVLaunchStatus(true);
    }
  }

  triggerProcessQueueOnResponse(responseInfo, checkWaiting = false) {
    // If setcontext response is received process the next request from queue
    if (
      responseInfo.topicName?.indexOf('SETCONTEXT_RESPONSE') > 0 ||
      responseInfo.topicName?.indexOf('CLOSE_RESPONSE') > 0
    ) {
      log.info('triggerProcessQueueOnResponse - setcontext/close response received process next request from queue');
      if (!checkWaiting || (checkWaiting && !!this.viewerMessageQueueParams.messageWaitingForResponse)) {
        this.processQueueOnSetcontextResponse(responseInfo);
      }
    }
  }

  /**
   * Check whether uViewer/iSite viewer hide/close response is pending
   */
  viewerHideOrCloseResponsePending() {
    if (this.viewerMessageQueueParams.messageWaitingForResponse !== undefined) {
      // iSite hide response is skipped because this cause issue when an iSite exam interrupts by UViewer exam
      if (
        this.viewerMessageQueueParams.messageWaitingForResponse.NCD === '' &&
        this.viewerMessageQueueParams.messageWaitingForResponse.accession === '' &&
        this.viewerMessageQueueParams.messageWaitingForResponse.viewerIdentifier !== 'iSite'
      ) {
        return true;
      }
    }
    return false;
  }

  testViewerStuck(actionName, accessionNumber, studyId) {
    return actionName === 'closediscard'
      ? this.viewerIsOpenedButNotAcknowledged(accessionNumber, studyId)
      : this.anotherViewerIsOpenedButNotAcknowledged(accessionNumber, studyId);
  }

  viewerIsOpenedButNotAcknowledged(accessionNumber, studyId) {
    if (this.viewerMessageQueueParams.messageWaitingForResponse !== undefined) {
      if (
        (this.viewerMessageQueueParams.messageWaitingForResponse.NCD !== '' &&
          this.viewerMessageQueueParams.messageWaitingForResponse.NCD === studyId) ||
        (this.viewerMessageQueueParams.messageWaitingForResponse.accession !== '' &&
          this.viewerMessageQueueParams.messageWaitingForResponse.accession === accessionNumber)
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check whether uViewer/iSite viewer launch response is pending for different context
   */
  anotherViewerIsOpenedButNotAcknowledged(accessionNumber, studyId) {
    if (this.viewerMessageQueueParams.messageWaitingForResponse !== undefined) {
      if (
        (this.viewerMessageQueueParams.messageWaitingForResponse.NCD !== '' &&
          this.viewerMessageQueueParams.messageWaitingForResponse.NCD !== studyId) ||
        (this.viewerMessageQueueParams.messageWaitingForResponse.accession !== '' &&
          this.viewerMessageQueueParams.messageWaitingForResponse.accession !== accessionNumber)
      ) {
        return true;
      }
    }
    return false;
  }

  performAction(actionName, workItem) {
    log.debug('Starting performAction workItem: ' + JSON.stringify(workItem) + ', actionName: ' + actionName);
    workItem.keys.source = 'desktop-integration';

    this.eventService
      .sendEvent(new EventMessage(Events[actionName], new EventWorkItem(workItem.id, workItem.keys)))
      .then(
        () => {
          log.info('performAction remote action ' + actionName + ', succeeded for Request ProcedureID ' + workItem.id);
        },
        error => {
          log.error(
            'performAction remote action ' +
              actionName +
              ' for failed with error: ' +
              error +
              'for Requested ProcedureID: ' +
              workItem.id
          );
        }
      );
    log.debug('Exiting performAction');
  }

  setViewerSetContextTimeout() {
    log.debug(
      'setViewerSetContextTimeout : setting timeout for viewer setcontext response : ' +
        this.diConfigService.UVIEWER_CONFIG.SETCONTEXT_TIMEOUT
    );
    // Timeout when setcontext response from viewer plugin is pending
    this.viewerSetContextTimeout = setTimeout(() => {
      if (this.viewerMessageQueueParams.messageWaitingForResponse !== undefined) {
        log.info('setViewerSetContextTimeout : timeout occurred while receiving the viewer response');
        // Clear all the pending requests in viewer queue.
        this.viewerMessageQueueParams.messageQueue = [];
        if (this.viewerMessageQueueParams.messageWaitingForResponse.NCD !== '') {
          // Hide all open viewers on viewer open timeout to avoid out of context issues
          this.notify.error(
            this.translate.instant('di.desktopIntegration.error.viewerResponsePendingOnTimeout'),
            this.translate.instant('di.desktopIntegration.title')
          );
          log.error('setViewerSetContextTimeout : call hideAllOpenViewers()');
          this.integrationStatusService.setViewerHealthStatus(
            this.integrationStatusService.healthStatusEnum.lastActionFailed,
            true
          );
          this.hideAllOpenViewers();
        } else if (
          this.viewerMessageQueueParams.messageWaitingForResponse.NCD === '' &&
          this.viewerMessageQueueParams.messageWaitingForResponse.viewerIdentifier === 'UViewer'
        ) {
          // Universal Viewer close time out scenario
          log.error('setViewerSetContextTimeout : Close failed - need to sign out the user');
          this.notifyAndSignUserOut();
          return;
        }
        this.viewerMessageQueueParams.messageWaitingForResponse = undefined;
        this.processViewerQueue();
      }
    }, this.diConfigService.UVIEWER_CONFIG.SETCONTEXT_TIMEOUT);
  }

  notifyAndSignUserOut() {
    // Disable desktop integration to avoid out of context
    this.viewerMessageQueueParams.messageQueue = [];
    this.integrationStatusService.disableDesktopIntegration();

    // Display critical message and then signout on dismissing the dialog box.
    this.notify
      .critical(
        this.translate.instant('di.desktopIntegration.critical.contextSwitchHideCloseFailure'),
        this.translate.instant('di.desktopIntegration.titleDesktopIntegrationDisabled')
      )
      .then(() => {
        log.info(
          'notifyAndSignUserOut - Context Switch Failure, signout automatically on' +
            ' dismissing the critical dialog box.'
        );
        this.signOutService.signOut();
      });
  }

  processViewerQueue() {
    if (this.viewerMessageQueueParams.messageWaitingForResponse === undefined) {
      const lsMessage =
        this.viewerMessageQueueParams.messageQueue.length > 0
          ? this.viewerMessageQueueParams.messageQueue.shift()
          : null;
      if (this.viewerMessageQueueParams.messageQueue.length > 0) {
        this.integrationStatusService.setViewerHealthStatus(
          this.integrationStatusService.healthStatusEnum.pendingResponse,
          true
        );
      }

      if (lsMessage !== null) {
        this.viewerMessageQueueParams.messageWaitingForResponse = lsMessage;
        log.info('processViewerQueue - calling invokeAPIWrapperExecute from processViewerQueue');
        this.diService.invokeAPIWrapperExecute(lsMessage.message);
        this.setViewerSetContextTimeout();
      } else {
        log.info('processViewerQueue - no message');
      }
    }
  }

  /**
   * Method to handle viewer plugin version mismatch response.
   */
  handleViewerVersionMismatchResponse() {
    // No need to call hidAllOpenViewers() method because version mismatch happens on first viewer launch
    log.info('handleViewerVersionMismatchResponse - Login failed due to UV version mismatch.');

    if (this.integrationStatusService.isUvActive()) {
      log.error(
        'handleViewerVersionMismatchResponse - Disabling viewer integration because' +
          ' SetContext failed with error code - ERR_VERSIONMISMATCH'
      );
      this.notify.critical(
        this.translate.instant('di.desktopIntegration.critical.versionMismatch'),
        this.translate.instant('di.desktopIntegration.titleViewerDisabled')
      );

      this.integrationStatusService.setViewerHealthStatus(this.integrationStatusService.healthStatusEnum.failed, true);
      // Clear viewer queue on disabling the viewer integration
      this.viewerMessageQueueParams.messageQueue = [];
    }
  }

  /**
   * Method to handle viewer plugin context switch failure response.
   */
  handleViewerContextswitchFailureResponse(responseInfo) {
    // Retry the failed request 2 times and still it is failure display warning message
    if (this.viewerRequestRetryCount < 2) {
      log.error('handleViewerContextswitchFailureResponse - CONTEXT_SWITCH_FAILURE retrying the' + ' last request');
      if (this.viewerMessageQueueParams.messageWaitingForResponse !== undefined) {
        // Adding the failed request in to first position in viewer queue and increment the retry count.
        this.viewerMessageQueueParams.messageQueue.unshift(this.viewerMessageQueueParams.messageWaitingForResponse);
        this.viewerRequestRetryCount++;
      }
    } else if (this.viewerRequestRetryCount === 2) {
      if (
        _.contains(responseInfo.failureTypes, 'CONTEXT_SWITCH_HIDE_FAILURE') ||
        _.contains(responseInfo.failureTypes, 'CONTEXT_SWITCH_CLOSE_FAILURE')
      ) {
        log.error('handleViewerContextswitchFailureResponse - CONTEXT_SWITCH_HIDE/CLOSE_FAILURE');
        this.integrationStatusService.setViewerHealthStatus(
          this.integrationStatusService.healthStatusEnum.failed,
          true
        );
        this.notifyAndSignUserOut();
      } else if (_.contains(responseInfo.failureTypes, 'CONTEXT_SWITCH_SHOW_FAILURE')) {
        // For compare studies context switch failure may occur due to invalid session
        if (this.utilService.getComparisons(this.getExamID(responseInfo.examIdent)) !== '') {
          const messageWaitingForResponse = this.viewerMessageQueueParams.messageWaitingForResponse;
          // Session is valid display notification and update the status in dashboard
          this.handleContextSwitchShowFailure(messageWaitingForResponse);
        } else {
          // For normal studies display notification and update the status in dashboard
          this.handleContextSwitchShowFailure(this.viewerMessageQueueParams.messageWaitingForResponse);
        }
      }
      // Increment the retry count to avoid multiple error message display
      this.viewerRequestRetryCount++;
    }
  }

  /**
   * Method to handle context switch show failure.
   */
  handleContextSwitchShowFailure(messageWaitingForResponse) {
    log.error('handleContextSwitchShowFailure - CONTEXT_SWITCH_SHOW_FAILURE');
    this.integrationStatusService.setViewerHealthStatus(
      this.integrationStatusService.healthStatusEnum.lastActionFailed,
      true
    );
    this.notify.warning(
      this.translate.instant('di.desktopIntegration.warning.contextSwitchShowFailure'),
      this.translate.instant('di.desktopIntegration.title')
    );
    if (messageWaitingForResponse.NCD !== '') {
      // Hide all open viewers on viewer open/discard failure to avoid out of context issues
      this.hideAllOpenViewers();
    }
  }

  /** Section : Utility methods */
  handleRemoteStudyCloseRequest(WorkItem) {
    try {
      if (WorkItem.id !== undefined) {
        PerformanceMonitorService.clear(PerformanceSource.VIEWER);
        log.debug('calling handleCloseExamRequest - study id : ' + WorkItem.id);
        if (this.contextService.checkStudyInContext(WorkItem.id)) {
          this.handleCloseExamRequest(WorkItem.id, WorkItem.actionType);
        }
      } else {
        log.error('close remote study event received, with workitem id undefined');
      }
    } catch (error) {
      log.error('failed on closing remote study with ' + error.description);
    }
  }

  private handleViewerLaunch(target, readOnlyExam) {
    if (target && target.id === this.contextService.getCurrentContext() && target.keys.disableAutoLaunchViewer) {
      log.info('Skip launch viewer');
      return;
    }
    this.handleLaunchRequest(target, readOnlyExam);
  }

  private closeExamRequest(WorkItem, actionName) {
    try {
      if (WorkItem.id !== undefined) {
        PerformanceMonitorService.clear(PerformanceSource.VIEWER);
        log.debug('calling handleCloseExamRequest - study id : ' + WorkItem.id);
        this.handleCloseExamRequest(WorkItem.id, WorkItem.actionType);
      } else {
        log.error(actionName + ' event received, with workitem id undefined');
      }
    } catch (error) {
      log.error('failed on ' + actionName + ' event with ' + error.description);
    }
  }

  /** Section : LineShaftPluginManager response handler */
  /**
   * Method to handle viewer plugin load response
   * @param responseInfo
   */
  handlePluginLoadResponseFromPM(responseInfo) {
    if (
      responseInfo.topicName === 'RadDesktop.LS_PLUGIN_LOAD_RESPONSE' &&
      responseInfo.sourceName === 'LineShaftPluginManager' &&
      responseInfo.moduleName === 'ViewerPlugin'
    ) {
      // LineShaftPluginManager internally wait for the load response from plugin and then only it will send back
      // the load response. So the result will contain the actual plugin load status also so we don't need to
      // explicitly wait for the response from plugin. If multiple applications are loading the plugin, at
      // LineShaft level itself it identifies the plugin is already loaded and send back the success response.
      this.handleViewerPluginLoadResponse(responseInfo);
    }
  }

  /**
   * Viewer, and CCOW setContext response handler.
   */
  LsPluginSetcontextResponseHandler(responseInfo) {
    // Check if viewer failed to launch
    // Launch viewer failed.
    if (
      responseInfo.topicName === 'RadDesktop.LS_PLUGIN_SETCONTEXT_RESPONSE' &&
      responseInfo.sourceName === 'ViewerPlugin'
    ) {
      /**
       * Viewer  plugin scenarios handled
       *  - viewer launched for different context
       *  - failed to launch viewer ( invalid session / context switch failure )
       *  - viewer version mismatch
       *  - mark viewer launch request acknowledgement
       *  - update viewer context  status Yes/No in contextService
       *  - publish viewerRequested event to enable/disable performance hook if launch success
       */
      this.handleViewerPluginSetcontextResponse(responseInfo);
    }
  }

  LsPluginCloseResponseHandler(responseInfo) {
    if (
      this.UVIEWER_CONFIG.OWNERINSTANCE + '.LS_PLUGIN_CLOSE_RESPONSE' === responseInfo.topicName &&
      responseInfo.moduleName === 'ViewerPlugin'
    ) {
      this.signOutService.expireJwtToken();
    }
  }

  LSPluginManagerResponseHandler(responseInfo) {
    if (
      responseInfo.topicName === 'RadDesktop.LS_PLUGIN_LOAD_RESPONSE' &&
      responseInfo.sourceName === 'LineShaftPluginManager' &&
      responseInfo.resultValue === 'FAILURE' &&
      responseInfo.moduleName === 'ViewerPlugin'
    ) {
      log.error('LSPluginManagerResponseHandler - Load plugin failed');
      this.handleViewerPluginLoadFailureFromPM();
    }
  }

  private handleViewerPluginLoadFailureFromPM() {
    log.error('handleViewerPluginLoadFailureFromPM - Viewer plugin load failed and viewer integration' + ' disabled');
    // No need to display viewer disabled critical message if it is already displayed from viewer load
    // timeout code block

    if (!this.integrationStatusService.getUViewerPluginStatus()) {
      this.notify.critical(
        this.translate.instant('di.desktopIntegration.critical.loadViewerFailed'),
        this.translate.instant('di.desktopIntegration.titleViewerDisabled')
      );
    }
    this.integrationStatusService.setUViewerPluginStatus(true);
    this.integrationStatusService.setViewerHealthStatus(this.integrationStatusService.healthStatusEnum.failed, true);
  }

  /** Section : handler functions */
  // Base functions
  private initializeLineShaftViewerPlugin() {
    this.utilService.loopUntilSuccessOrMaxRetry((lastAttempt: boolean) => {
      const retObj = { loadErrorEntity: [] };
      this.subscribePluginResponse(retObj);
      this.loadPlugin(retObj);

      if (lastAttempt && retObj.loadErrorEntity.length !== 0) {
        // If Viewer plugin load failed, display critical error message
        log.error('loadPluginHandler - Viewer plugin load failed');
        this.notify.critical(
          this.translate.instant('di.desktopIntegration.critical.loadViewerFailed'),
          this.translate.instant('di.desktopIntegration.titleViewerDisabled')
        );
      }
      return retObj.loadErrorEntity.length === 0;
    }, this.uvModuleName);
  }

  /** Section : Methods used during Constructor initialization */
  private setConfigValues() {
    this.tabs = this.utilService.sharedTabContext;
    this.loadMessageQueueParams = this.diService.LoadMessageQueueParams;
    this.viewerMessageQueueParams = this.utilService.getViewerMessageQueue();
    this.UVIEWER_CONFIG = this.diConfigService.UVIEWER_CONFIG;
    this.uvModuleName = this.UVIEWER_CONFIG.MODULENAME;
  }

  subscribeListeners() {
    // Lineshaft channel
    this.lineShaftHandlerService.channelObservers.onReady.subscribe(startLoad => {
      if (startLoad) {
        log.info('LineShaft registration started');
        this.initializeLineShaftViewerPlugin();
      }
    });
    this.lineShaftHandlerService.channelObservers.onResponseMessage.subscribe(({ responseInfo, _responseXML }) => {
      log.debug('LineShaft message processing', responseInfo);
      this.ViewerCloseResponseHandler(responseInfo);
      this.ViewerErrorResponseHandler(responseInfo);
      this.docScanResponseHandler(responseInfo);
      this.handlePluginLoadResponseFromPM(responseInfo);
      this.LsPluginSetcontextResponseHandler(responseInfo);
      // processing of the setcontext queue should be done only after handling the response from viewer.
      // The retry logic won't work otherwise. otherwise, it will end-up user cannot move to next study.
      this.triggerProcessQueueOnResponse(responseInfo, true);
      this.LSPluginManagerResponseHandler(responseInfo);
      this.LsPluginCloseResponseHandler(responseInfo);
    });
    this.lineShaftHandlerService.channelObservers.onLifecycleChange.subscribe(response => {
      if (response?.Component === 'Viewer') {
        log.debug('universalViewer lineshaft lifecycle message received' + response);
        this.handleViewerResponse(response);
      }
    });

    // Events
    this.messageService.on('closeDiscardForRemoteStudy').subscribe(data => {
      this.handleRemoteStudyCloseRequest(data);
    });
    this.messageService.on('resetPlugin').subscribe(() => {
      if (this.integrationStatusService.isUvActive()) {
        this.integrationStatusService.setViewerHealthStatus('init', true);
        this.integrationStatusService.setUViewerPluginStatus(false);
        this.integrationStatusService.setUVLaunchStatus(false);
        this.viewerMessageQueueParams.messageWaitingForResponse = undefined;
        this.viewerMessageQueueParams.messageQueue = [];
        const tab = this.utilService.getActiveTab();
        if (tab) {
          // Set the openViewer flag to relaunch the current context viewer after resetting the plugins
          tab.workItem.keys.openViewer = true;
        }
      }
    });
    this.messageService.on('refreshDXHub').subscribe(data => {
      this.refresh = data.refresh;
    });
  }

  private beforeEventActions() {
    _.each(['autoAdvanceSkipAssign', 'autoAdvanceSkipUnAssign', 'externalAccessTab'], actionName => {
      this.eventService.beforeEvent(actionName, (lifecycle, targets) => {
        const viewerContext = this.checkViewerContext(actionName, targets.id);
        if (viewerContext) {
          this.integrationStatusService.setViewerHealthStatus(
            this.integrationStatusService.healthStatusEnum.pendingResponse,
            true
          );
          log.info('before action ' + actionName + ' - viewer launch response is stuck,' + ' halting the action.');
          log.error('Halted in desktop Integrtaion Controler');
          lifecycle.halt('DESKTOPINTEGRATIONHALTED');
          return;
        }
        if (actionName === 'autoAdvanceSkipUnAssign' || actionName === 'autoAdvanceSkipAssign') {
          this.closeExamRequest(targets, actionName);
        }
        lifecycle.done();
      });
    });
  }

  private afterEventActions() {
    _.each(['preview', 'externalAccessTab'], actionName => {
      this.eventService.afterEvent(actionName, target => {
        const studyId = target.id;
        log.info('worklistSelect event received - requestedProcedureId : ' + studyId);
        if (this.refresh) {
          this.refresh = false;
          return;
        }
        try {
          this.handlePreviewRequest(target);
        } catch (error) {
          log.error('failed preview for studyId' + studyId + ' with error: ' + error);
        }
      });
    });

    _.each(['read', 'takeControl'], actionName => {
      this.eventService.afterEvent(
        actionName,
        target => {
          try {
            if (this.refresh) {
              this.refresh = false;
              return;
            }
            log.debug('read action handler - Id : ' + target.id + ' Acc: ' + target.keys.procedureAccessionNo);
            log.debug('read action handler - Id : ' + target.id + ' Site name Key: ' + target.keys.siteNameKey);
            const readOnlyFlag = false;
            if (target.keys.openViewer) {
              // Interrupt scenario, need to reset the studyclick time
              target.keys.studyClickTime = new Date().getTime();
              this.handleViewerLaunch(target, readOnlyFlag);

              delete target.keys.openViewer;
            }
            // The readonly flag needs to be set since the exam is already launched in launchreadonly action.
            // This flag is used in auto advance workflow for viewerclosed.actslike.reportsaved
            const context = this.contextService.getContextItem(target.id);
            if (!!context) {
              context.readOnlyExam = false;
            }
          } catch (error) {
            log.error(
              'failed on read action handler - Id : ' +
                target.id +
                ' Acc: ' +
                target.keys.procedureAccessionNo +
                ' with error: ' +
                error
            );
          }
        },
        EventType.LOCAL
      );
    });

    _.each(['clear', 'close'], actionName => {
      this.eventService.afterEvent(
        actionName,
        context => {
          const contextId = context.id;
          if (contextId !== undefined) {
            log.debug('readAndCloseExam event received : ' + contextId);
            this.saveAndCloseViewer(contextId);
          }
        },
        EventType.LOCAL
      );
    });

    this.eventService.afterEvent(
      Events.closeviewer.name,
      context => {
        const contextId = context.id;
        if (contextId !== undefined) {
          log.debug('readAndCloseExam event received : ' + contextId);
          this.viewerSavedEventHandler(contextId);
        }
      },
      EventType.LOCAL
    );

    this.eventService.afterEvent(Events.launchreadonly.name, target => {
      try {
        if (this.refresh) {
          this.refresh = false;
          return;
        }
        log.info('launchreadonly event handler - Study Id : ' + target.id + ' ' + JSON.stringify(target));
        const readOnlyExam = true;
        this.handleViewerLaunch(target, readOnlyExam);
      } catch (error) {
        log.error('failed on launchreadonly action handler with ' + error);
      }
    });

    this.eventService.afterEvent(
      Events.closediscard.name,
      WorkItem => {
        this.closeExamRequest(WorkItem, Events.closediscard.name);
      },
      EventType.LOCAL
    );
  }
}
